/**
 * Chat history export — the four file formats (PLAN §8). Pure:
 * `(rows, meta) => string`, where rows come from chatExportModel.toExportRow
 * and meta is { channelName, channelKind, exportedAt, exportedBy, members[],
 * truncated, historyDays }.
 *
 * The file's own vocabulary (Exported, Members, Note, …) is English on
 * purpose (owner Q5): a file is an artefact, and the JSON keys must not change
 * with the UI language. Dates and times use the viewer's locale; JSON carries
 * ISO UTC.
 *
 * HTML is self-contained and inert: inline <style> only — no src=, <link>,
 * <script> or @import — and every user string goes through escapeHtml.
 */

export function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** A code fence longer than any backtick run inside the code (min 3). */
export function fenceFor(code) {
  const runs = String(code || '').match(/`+/g) || [];
  const longest = runs.reduce((n, r) => Math.max(n, r.length), 0);
  return '`'.repeat(Math.max(3, longest + 1));
}

export function formatSize(bytes) {
  if (!Number.isFinite(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const pad = (n) => String(n).padStart(2, '0');
const isoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const timeOf = (d) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
const longDay = (d) => d.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

/** Rows grouped by the viewer's calendar day, in order: [{ date, rows }]. */
export function groupByDay(rows) {
  const groups = [];
  for (const row of rows) {
    const d = row.at ? new Date(row.at) : null;
    const key = d ? dayKey(d) : 'unknown';
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.rows.push(row);
    else groups.push({ key, date: d, rows: [row] });
  }
  return groups;
}

const quote = (s) => `“${s || 'Untitled'}”`;

function appointmentWhen(share) {
  if (!share.start) return '';
  const s = new Date(share.start);
  if (Number.isNaN(s.getTime())) return '';
  if (share.all_day) return s.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' });
  const e = share.end ? new Date(share.end) : null;
  const day = s.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' });
  return `${day} ${timeOf(s)}${e && !Number.isNaN(e.getTime()) ? `–${timeOf(e)}` : ''}`;
}

/** One-line description of a share / legacy reference (all text formats). */
export function shareLine(share) {
  if (!share) return '';
  if (share.type === 'task') return '✅ Shared a task';
  if (share.type === 'appointment') {
    const when = appointmentWhen(share);
    const where = share.location ? `, ${share.location}` : '';
    return `📅 Appointment ${quote(share.title)}${when ? ` — ${when}` : ''}${where}`;
  }
  let extra = '';
  if (share.type === 'request' && share.method) extra = ` (${share.method})`;
  else if (share.type === 'collection' && share.item_count != null) extra = ` (${share.item_count} requests)`;
  else if (share.type === 'workspace' && share.item_count != null) extra = ` (${share.item_count} collections)`;
  const ws = share.workspace_name ? ` in ${share.workspace_name}` : '';
  return `🔗 Shared ${share.type} ${quote(share.label)}${extra}${ws}`;
}

const reactionSummary = (r) => `${r.emoji} ${r.count}${r.users.length ? ` (${r.users.join(', ')})` : ''}`;
const senderName = (row) => row.sender?.name || 'Unknown';

function notes(meta) {
  const out = [];
  if (meta.truncated) {
    out.push(`This export stopped after ${meta.count} messages (the export limit). Later messages are not included.`);
  }
  if (meta.historyDays) {
    out.push(`Your plan shows the last ${meta.historyDays} days of this conversation; older messages are not included.`);
  }
  return out;
}

function headerFacts(rows, meta) {
  const at = meta.exportedAt instanceof Date ? meta.exportedAt : new Date(meta.exportedAt || Date.now());
  return [
    ['Exported', `${at.toLocaleDateString()} ${timeOf(at)}`],
    ['By', meta.exportedBy || ''],
    ['Members', (meta.members || []).join(', ')],
    ['Messages', String(rows.length)],
  ];
}

// ── Markdown ────────────────────────────────────────────────────────────────

function mdMessage(row) {
  const d = row.at ? new Date(row.at) : null;
  const head = [`**${senderName(row)}**`, d ? timeOf(d) : null, row.edited ? 'edited' : null, row.forwarded ? 'forwarded' : null]
    .filter(Boolean)
    .join(' · ');
  const lines = [head];
  if (row.reply) lines.push(`> ↪ ${row.reply.sender_name || 'Unknown'}: ${row.reply.body}`);
  if (row.code) {
    const fence = fenceFor(row.code.source);
    lines.push(`${fence}${row.code.language === 'plaintext' ? '' : row.code.language}`, row.code.source, fence);
  }
  if (row.attachment) {
    const a = row.attachment;
    const facts = [formatSize(a.size), a.mime].filter(Boolean).join(', ');
    lines.push(`📎 ${a.name || 'attachment'}${facts ? ` (${facts})` : ''}`);
  }
  if (row.share) lines.push(shareLine(row.share));
  if (row.secret) lines.push(`🔒 Vault secret: ${row.secret.label || 'Untitled'}`);
  if (row.text) lines.push(row.text);
  if (row.reactions.length) lines.push(`_Reactions: ${row.reactions.map(reactionSummary).join(', ')}_`);
  return lines.join('\n');
}

export function toMarkdown(rows, meta = {}) {
  const parts = [`# ${meta.channelName || 'Chat'}`, headerFacts(rows, meta).map(([k, v]) => `- ${k}: ${v}`).join('\n')];
  for (const g of groupByDay(rows)) {
    parts.push(`## ${g.date ? longDay(g.date) : 'Unknown date'}`);
    g.rows.forEach((row) => parts.push(mdMessage(row)));
  }
  notes({ ...meta, count: rows.length }).forEach((n) => parts.push(`> Note: ${n}`));
  return `${parts.join('\n\n')}\n`;
}

// ── Plain text ──────────────────────────────────────────────────────────────

const indent = (s, by) => s.split('\n').map((l) => by + l).join('\n');

function textMessage(row) {
  const d = row.at ? new Date(row.at) : null;
  const stamp = d ? `[${isoDay(d)} ${timeOf(d)}] ` : '';
  const flags = [row.edited ? '(edited)' : null, row.forwarded ? '(forwarded)' : null].filter(Boolean).join(' ');
  const lines = [`${stamp}${senderName(row)}:${flags ? ` ${flags}` : ''}`];
  if (row.reply) lines.push(`  ↪ ${row.reply.sender_name || 'Unknown'}: ${row.reply.body}`);
  if (row.code) lines.push(indent(row.code.source, '    '));
  if (row.attachment) {
    const size = formatSize(row.attachment.size);
    lines.push(`  [attachment] ${row.attachment.name || 'attachment'}${size ? ` (${size})` : ''}`);
  }
  if (row.share) lines.push(`  ${shareLine(row.share)}`);
  if (row.secret) lines.push(`  [vault secret] ${row.secret.label || 'Untitled'}`);
  if (row.text) lines.push(indent(row.text, '  '));
  if (row.reactions.length) lines.push(`  (reactions: ${row.reactions.map((r) => `${r.emoji}×${r.count}`).join(' ')})`);
  return lines.join('\n');
}

export function toPlainText(rows, meta = {}) {
  const head = [meta.channelName || 'Chat', ...headerFacts(rows, meta).map(([k, v]) => `${k}: ${v}`), '===='];
  const parts = [head.join('\n')];
  for (const g of groupByDay(rows)) {
    parts.push(`--- ${g.date ? isoDay(g.date) : 'unknown date'} ---`);
    g.rows.forEach((row) => parts.push(textMessage(row)));
  }
  notes({ ...meta, count: rows.length }).forEach((n) => parts.push(`NOTE: ${n}`));
  return `${parts.join('\n\n')}\n`;
}

// ── JSON ────────────────────────────────────────────────────────────────────

export function toJson(rows, meta = {}) {
  const at = meta.exportedAt instanceof Date ? meta.exportedAt : new Date(meta.exportedAt || Date.now());
  const doc = {
    format: 'apiboo.chat-export',
    version: 1,
    channel: { name: meta.channelName || null, kind: meta.channelKind || null },
    exported_at: at.toISOString(),
    exported_by: meta.exportedBy || null,
    members: meta.members || [],
    truncated: Boolean(meta.truncated),
    history_days: meta.historyDays ?? null,
    messages: rows,
  };
  return `${JSON.stringify(doc, null, 2)}\n`;
}

// ── HTML ────────────────────────────────────────────────────────────────────

const HTML_STYLE = `
body{font:14px/1.5 system-ui,-apple-system,"Segoe UI",sans-serif;margin:0;background:#fff;color:#111827}
main{max-width:820px;margin:0 auto;padding:24px 16px}
h1{font-size:20px;margin:0 0 8px}
dl{display:grid;grid-template-columns:max-content 1fr;gap:2px 12px;color:#6b7280;margin:0 0 16px}
dt{font-weight:600}dd{margin:0}
h2.day{font-size:12px;font-weight:600;color:#6b7280;text-align:center;border-top:1px solid #e5e7eb;padding-top:8px;margin:24px 0 8px}
.msg{padding:6px 0}
.who{font-weight:600}
time{color:#6b7280;font-size:12px;margin-left:8px}
.flag{color:#9ca3af;font-size:12px;margin-left:6px}
.body{white-space:normal;word-break:break-word}
blockquote.reply{margin:2px 0 4px;padding-left:8px;border-left:3px solid #d1d5db;color:#6b7280}
pre{background:#f3f4f6;border:1px solid #e5e7eb;border-radius:6px;padding:8px;overflow:auto}
.att,.share,.secret{display:inline-block;border:1px solid #e5e7eb;border-radius:6px;padding:2px 8px;margin:2px 0}
.reacts{color:#6b7280;font-size:12px}
.note{color:#6b7280;border-left:3px solid #d1d5db;padding-left:8px}
a{color:#7c5ce6}
@media (prefers-color-scheme: dark){
body{background:#18181b;color:#f3f4f6}
dl,time,.reacts,.note,blockquote.reply,h2.day{color:#9ca3af}
h2.day{border-color:#26262b}
pre{background:#0f0f12;border-color:#26262b}
.att,.share,.secret{border-color:#26262b}
blockquote.reply,.note{border-color:#3f3f46}
a{color:#a78bfa}
}`;

/** Escape, keep line breaks, and turn http(s)/www links into anchors. */
function htmlText(s) {
  return escapeHtml(s)
    .replace(/(https?:\/\/[^\s<]+|www\.[^\s<]+)/g, (url) => {
      const href = url.startsWith('www.') ? `https://${url}` : url;
      return `<a href="${href}" rel="noopener noreferrer">${url}</a>`;
    })
    .replace(/\n/g, '<br>');
}

function htmlMessage(row) {
  const d = row.at ? new Date(row.at) : null;
  const parts = [`<div class="msg"><span class="who">${escapeHtml(senderName(row))}</span>`];
  if (d) parts.push(`<time datetime="${escapeHtml(row.at)}">${escapeHtml(timeOf(d))}</time>`);
  if (row.edited) parts.push('<span class="flag">edited</span>');
  if (row.forwarded) parts.push('<span class="flag">forwarded</span>');
  if (row.reply) {
    parts.push(`<blockquote class="reply">↪ ${escapeHtml(row.reply.sender_name || 'Unknown')}: ${escapeHtml(row.reply.body)}</blockquote>`);
  }
  if (row.code) {
    parts.push(`<pre><code class="language-${escapeHtml(row.code.language)}">${escapeHtml(row.code.source)}</code></pre>`);
  }
  if (row.attachment) {
    const size = formatSize(row.attachment.size);
    parts.push(`<span class="att">📎 ${escapeHtml(row.attachment.name || 'attachment')}${size ? ` (${escapeHtml(size)})` : ''}</span>`);
  }
  if (row.share) parts.push(`<span class="share">${escapeHtml(shareLine(row.share))}</span>`);
  if (row.secret) parts.push(`<span class="secret">🔒 ${escapeHtml(row.secret.label || 'Untitled')}</span>`);
  if (row.text) parts.push(`<div class="body">${htmlText(row.text)}</div>`);
  if (row.reactions.length) {
    parts.push(`<div class="reacts">${escapeHtml(row.reactions.map(reactionSummary).join(', '))}</div>`);
  }
  parts.push('</div>');
  return parts.join('');
}

export function toHtml(rows, meta = {}) {
  const title = escapeHtml(meta.channelName || 'Chat');
  const out = [
    '<!doctype html>',
    '<html lang="en"><head><meta charset="utf-8">',
    '<meta name="viewport" content="width=device-width, initial-scale=1">',
    `<title>${title} — chat export</title>`,
    `<style>${HTML_STYLE}</style></head><body><main>`,
    `<h1>${title}</h1>`,
    `<dl>${headerFacts(rows, meta).map(([k, v]) => `<dt>${k}</dt><dd>${escapeHtml(v)}</dd>`).join('')}</dl>`,
  ];
  for (const g of groupByDay(rows)) {
    out.push(`<h2 class="day">${escapeHtml(g.date ? longDay(g.date) : 'Unknown date')}</h2>`);
    g.rows.forEach((row) => out.push(htmlMessage(row)));
  }
  notes({ ...meta, count: rows.length }).forEach((n) => out.push(`<p class="note">Note: ${escapeHtml(n)}</p>`));
  out.push('</main></body></html>');
  return `${out.join('\n')}\n`;
}

export const FORMATTERS = { markdown: toMarkdown, json: toJson, text: toPlainText, html: toHtml };
